import {
  Component,
  ContentChildren,
  QueryList,
  Input,
  forwardRef,
  AfterViewInit,
  OnDestroy,
  HostListener,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  TemplateRef,
  ViewContainerRef,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { trigger, style, transition, animate, state } from "@angular/animations";
import { DsOptionComponent } from "../ds-selector-components/ds-option/ds-option.component";
import { Colors, Sizes } from "../types/selector.types";
import { isEqual } from "lodash-es";
import { DsOptionService } from "../../services/ds-option.service";
import { Overlay, OverlayPositionBuilder, OverlayRef } from "@angular/cdk/overlay";
import { AnimationStates } from "../types/tooltips.types";

@Component({
  selector: "ds-tag-selector",
  templateUrl: "./ds-tag-selector.component.html",
  styleUrls: ["./ds-tag-selector.component.scss"],
  animations: [
    trigger("fadeInOut", [
      state(
        "hidden",
        style({
          opacity: 0,
          transform: "scale(0.8) translateY(-8px)",
        })
      ),
      state(
        "visible",
        style({
          opacity: 1,
          transform: "scale(1) translateY(0)",
        })
      ),
      transition("hidden => visible", [animate("300ms cubic-bezier(0.25, 0.8, 0.25, 1)")]),
      transition("visible => hidden", [animate("300ms cubic-bezier(0.4, 0, 0.2, 1)")]),
    ]),
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DsTagSelectorComponent),
      multi: true,
    },
  ],
})
export class DsTagSelectorComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @Input() disabled = false;
  @Input() placeholder: string = "";
  @Input() color: Colors = "primary";
  @Input() size: Sizes = "sm";
  @Input() error = false;
  @Input() alwaysOpen = false;
  @Input() emptyOptionsText: string = "Sin opciones";

  @ContentChildren(DsOptionComponent, { descendants: true }) options!: QueryList<DsOptionComponent>;
  @ViewChild("selectorElement", { static: false }) selectorElement!: ElementRef<HTMLDivElement>;
  @ViewChild("optionListContent") optionListContent!: TemplateRef<any>;

  isOpen = false;
  value: any = null;
  isAnimating = false;
  highlightedIndex: number = -1;
  lastSearchLetter: string = "";
  lastSearchIndex: number = -1;
  animationState: AnimationStates = "hidden";
  overlayRef: OverlayRef | null = null;
  private resizeObserver: ResizeObserver | null = null;

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(
    private elementRef: ElementRef,
    private dsOptionService: DsOptionService,
    private cdr: ChangeDetectorRef,
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private vcr: ViewContainerRef
  ) {}

  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    if (!this.isOpen) {
      if (event.key === "Enter") {
        event.preventDefault();
        this.togglePanel();
      }
      return;
    }

    const enabledOptions = this.dsOptionService.getEnabledOptions(this.options);
    if (enabledOptions.length === 0) return;
    if (event.key.length === 1 && /^[a-zA-Z]$/.test(event.key)) {
      event.preventDefault();
      const letter = event.key;

      const isSameLetter = letter.toLowerCase() === this.lastSearchLetter.toLowerCase();
      const startIndex = isSameLetter ? this.lastSearchIndex + 1 : 0;

      const foundIndex = this.dsOptionService.findOptionByLetter(this.options, letter, startIndex);

      if (foundIndex >= 0) {
        this.highlightedIndex = foundIndex;
        this.lastSearchLetter = letter;
        this.lastSearchIndex = foundIndex;
        this.updateOptionsHighlight();
        this.scrollToHighlightedOption();
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.highlightedIndex = this.highlightedIndex < enabledOptions.length - 1 ? this.highlightedIndex + 1 : 0;
        this.updateOptionsHighlight();
        this.scrollToHighlightedOption();
        break;
      case "ArrowUp":
        event.preventDefault();
        this.highlightedIndex = this.highlightedIndex > 0 ? this.highlightedIndex - 1 : enabledOptions.length - 1;
        this.updateOptionsHighlight();
        this.scrollToHighlightedOption();
        break;
      case "Enter":
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < enabledOptions.length) {
          this.selectOption(enabledOptions[this.highlightedIndex]);
        }
        break;
      case "Escape":
        event.preventDefault();
        this.closePanel();
        break;
    }
  }

  writeValue(value: any): void {
    this.value = value;
    this.updateOptionsSelection();
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngAfterViewInit() {
    this.options.changes.subscribe(() => {
      this.setupOptionListeners();
      setTimeout(() => {
        this.updateOptionsSelection();
        this.cdr.detectChanges();
      });
    });
    this.setupOptionListeners();

    setTimeout(() => {
      this.updateOptionsSelection();
      this.cdr.detectChanges();
    });
  }

  private setupOptionListeners() {
    this.options.forEach((option) => {
      option.optionSelected.subscribe((selectedOption: DsOptionComponent) => {
        this.selectOption(selectedOption);
      });
    });
  }

  togglePanel() {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    this.showOptions();
  }

  closePanel() {
    if (this.isOpen) {
      this.isOpen = false;
      this.highlightedIndex = -1;
      this.lastSearchLetter = "";
      this.lastSearchIndex = -1;
      this.updateOptionsHighlight();
      this.hideOptions();
      this.onTouched();
    }
  }

  selectOption(option: DsOptionComponent) {
    this.value = option.value;
    this.isOpen = false;
    this.hideOptions();
    this.highlightedIndex = -1;
    this.onChange(this.value);
    this.onTouched();
    this.updateOptionsSelection();
  }

  private updateOptionsSelection() {
    if (!this.options) return;

    this.options.forEach((opt) => {
      opt.selected = isEqual(this.value, opt.value);
    });
    if (this.isOpen) {
      this.updateOptionsHighlight();
    }
  }

  private updateOptionsHighlight(): void {
    this.dsOptionService.updateOptionsHighlight(this.options, this.highlightedIndex);
  }

  private scrollToHighlightedOption(): void {
    const container = this.overlayRef?.overlayElement ?? this.elementRef.nativeElement;
    this.dsOptionService.scrollToHighlightedOption(this.options, this.highlightedIndex, container);
  }

  getDisplayValue(): string {
    if (!this.value) {
      return this.placeholder || "";
    }
    const selectedOption = this.options?.find((option) => isEqual(option.value, this.value));

    return selectedOption?.displayText || this.value;
  }

  get hasOptions(): boolean {
    if (!this.options) return false;
    const realOptions = this.options.filter((opt) => !opt.default);
    return realOptions.length > 0;
  }

  ngOnDestroy(): void {
    this.stopResizeObserver();
  }

  showOptions = () => {
    if (this.overlayRef) return;
    this.isOpen = true;

    const element = this.selectorElement.nativeElement;
    const positionStrategy = this.dsOptionService.createPositionStrategy(this.overlayPositionBuilder, element);

    this.overlayRef = this.dsOptionService.createOverlay(this.overlay, positionStrategy, element);
    this.dsOptionService.setupPositionChangeListener(positionStrategy, element, this.overlayRef);

    this.overlayRef.detachments().subscribe(() => this.onOverlayClosed());
    this.dsOptionService.attachTemplate(this.overlayRef, this.optionListContent, this.vcr);
    this.startResizeObserver();

    setTimeout(() => document.addEventListener("click", this.onDocumentClick));
    requestAnimationFrame(() => this.overlayRef?.updatePosition());
    setTimeout(() => (this.animationState = "visible"));

    const enabledOptions = this.dsOptionService.getEnabledOptions(this.options);
    if (enabledOptions.length > 0) {
      const selectedIndex = enabledOptions.findIndex((opt) => opt.selected);
      this.highlightedIndex = selectedIndex >= 0 ? selectedIndex : 0;
      setTimeout(() => {
        this.updateOptionsHighlight();
        requestAnimationFrame(() => setTimeout(() => this.scrollToHighlightedOption(), 50));
        this.selectorElement?.nativeElement.focus();
      }, 100);
    }
  };

  private onOverlayClosed() {
    document.removeEventListener("click", this.onDocumentClick);
    this.stopResizeObserver();
    this.animationState = "hidden";
    this.overlayRef = null;
  }

  hideOptions = () => {
    if (!this.overlayRef) return;
    this.isOpen = false;
    document.removeEventListener("click", this.onDocumentClick);
    this.stopResizeObserver();
    this.animationState = "hidden";

    this.dsOptionService.disposeOverlay(this.overlayRef).then(() => (this.overlayRef = null));

    this.highlightedIndex = -1;
    this.lastSearchLetter = "";
    this.lastSearchIndex = -1;
    this.updateOptionsHighlight();
  };

  private onDocumentClick = (event: MouseEvent) => {
    if (this.dsOptionService.isClickOutside(event, this.selectorElement?.nativeElement, this.overlayRef)) {
      this.hideOptions();
    }
  };

  private startResizeObserver(): void {
    if (this.overlayRef) {
      this.resizeObserver = this.dsOptionService.createResizeObserver(
        this.selectorElement.nativeElement,
        this.overlayRef
      );
    }
  }

  private stopResizeObserver(): void {
    this.resizeObserver = this.dsOptionService.stopResizeObserver(this.resizeObserver);
  }
}
